// /console/:slug — ONE project's console, hosted by the platform.
//
// THE console (src/project/), rendered here with that project's config
// injected — there is exactly one console deployment, this one; the old
// `{slug}.s3p.dev/server` URL just redirects here. Works for every project you
// operate — the platform's own project included (`/console/console`), because
// the platform is just another project.
//
// The project SWITCHER is not here: it's in ConsoleLayout's top bar, above this
// panel, where switching project reads as navigation rather than as a setting of
// the panel you happen to be looking at.
//
// Security is unchanged by hosting it here. The console signs into the PLATFORM
// pool and exchanges that token at the TARGET project's Identity Pool, which maps
// `{slug}:operator-owner|-admin` to that project's role with a Deny fall-through.
// The config we inject names resources; it grants nothing. Open a console for a
// project you don't operate and you get no credentials — verified against the
// live pools.

import { Alert, Group, Loader } from "@mantine/core";
import { Suspense, lazy } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useOperableProjects } from "../../hooks/useOperableProjects.ts";
import { projectPath } from "../../lib/hosts.ts";
import { configFromManifest, isFederated } from "../../lib/project-config.ts";

// Code-split: the panels are the heaviest part of the app, and only this route
// shows them.
const ProjectConsoleView = lazy(() => import("../../project/ProjectConsoleView.tsx"));

export function ProjectConsole() {
  const { slug = "" } = useParams();
  const navigate = useNavigate();

  const projects = useOperableProjects();
  const manifest = (projects.data ?? []).find((p) => p.slug === slug) ?? null;

  if (projects.loading) {
    return (
      <Group justify="center" py={80}>
        <Loader />
      </Group>
    );
  }

  if (!manifest) {
    return (
      <div style={{ maxWidth: 700, margin: "0 auto", padding: "48px 32px" }}>
        <Alert color="red" variant="light" title={`No project '${slug}'`}>
          You don't operate a project with that slug — or it hasn't finished provisioning. Ask an
          owner to grant you access from that project's console → Team.
        </Alert>
      </div>
    );
  }

  // FAIL AT THE DOOR, with the fix. A pre-federation project used to get a
  // FABRICATED consoleAuth here: sign-in succeeded, then every panel died with
  // AccessDenied because the project's Identity Pool never trusted the platform
  // pool. That's the worst kind of broken — it looks open.
  if (!isFederated(manifest)) {
    return (
      <div style={{ maxWidth: 700, margin: "0 auto", padding: "48px 32px" }}>
        <Alert color="yellow" variant="light" title="This project predates operator federation">
          Its Identity Pool doesn't trust platform identities yet, so this console can't sign in. An
          operator can wire it up with:
          <pre style={{ marginTop: 8 }}>{`s3p-admin converge --slug ${slug} --execute`}</pre>
        </Alert>
      </div>
    );
  }

  const spinner = (
    <Group justify="center" py={80}>
      <Loader size="sm" />
    </Group>
  );
  return (
    <Suspense fallback={spinner}>
      {/* Keyed on the slug: switching project builds a fresh host for it. */}
      <ProjectConsoleView
        key={slug}
        config={configFromManifest(manifest)}
        basePath={projectPath(slug)}
        navigate={navigate}
      />
    </Suspense>
  );
}
